export function hasPermissionAction(permissions, action) {
  if (!action || !Array.isArray(permissions) || permissions.length === 0) {
    return false;
  }

  const tokens = String(action)
    .split("||")
    .map((token) => String(token).trim().toUpperCase())
    .filter(Boolean);

  if (!tokens.length) return false;

  const normalizedPermissions = permissions.map((permission) =>
    String(permission).trim().toUpperCase()
  );

  return tokens.some((token) => normalizedPermissions.includes(token));
}

/** User Management — list / route access. */
export const USER_LIST_ACCESS = "USER_VIEW_ALL";

export const USER_ADD_ACTIONS = "USER_ADD";
export const USER_EDIT_ACTIONS = "USER_EDIT";
export const USER_DELETE_ACTIONS = "USER_DELETE";

export const USER_VIEW_ACTIONS = "USER_VIEW_ALL||USER_ALL";

export const USER_ROLE_LIST_ACCESS = "USER_ROLE_ALL";

export const USER_ROLE_ADD_ACTIONS = "USER_ROLE_ADD";
export const USER_ROLE_EDIT_ACTIONS = "USER_ROLE_EDIT";
export const USER_ROLE_DELETE_ACTIONS = "USER_ROLE_DELETE";

export const USER_ROLE_VIEW_ACTIONS = "USER_ROLE_ALL";

export const USER_PERMISSION_LIST_ACCESS =
  "USER_PERMISSION_ALL||USER_PERMISSION_ADD||USER_PERMISSION_EDIT||USER_PERMISSION_DELETE";

export const USER_PERMISSION_ADD_ACTIONS = "USER_PERMISSION_ADD";
export const USER_PERMISSION_EDIT_ACTIONS = "USER_PERMISSION_EDIT";
export const USER_PERMISSION_DELETE_ACTIONS = "USER_PERMISSION_DELETE";

/** User Role Permission module — list / route access. */
export const USER_ROLE_PERMISSION_LIST =
  "USER_ROLE_PERMISSION||USER_ROLE_PERMISSION_ALL||USER_ROLE_PERMISSION_ASSIGN||USER_ROLE_PERMISSION_REMOVE||USER_ROLE_PERMISSION_ADD||USER_ROLE_PERMISSION_EDIT||USER_ROLE_PERMISSION_DELETE";

/** List page: add new role-permission mapping. */
export const USER_ROLE_PERMISSION_ADD_ACTIONS = "USER_ROLE_PERMISSION_ADD";

/** List page: open edit / navigate to edit form. */
export const USER_ROLE_PERMISSION_EDIT_ACTIONS = "USER_ROLE_PERMISSION_EDIT";

/** List page: delete entire role-permission configuration for a role. */
export const USER_ROLE_PERMISSION_DELETE_ACTIONS = "USER_ROLE_PERMISSION_DELETE";

/** Form only: check/assign a permission on a role. */
export const USER_ROLE_PERMISSION_ASSIGN_ACTIONS = "USER_ROLE_PERMISSION_ASSIGN";

/** Form only: uncheck/remove a permission from a role. */
export const USER_ROLE_PERMISSION_REMOVE_ACTIONS = "USER_ROLE_PERMISSION_REMOVE";

/**
 * CRUD button checks: exact permission(s) listed in action (|| alternates).
 * USER_ROLE_PERMISSION_ALL and USER_ROLE_PERMISSION are list/view only — not CRUD.
 * On the edit form, USER_ROLE_PERMISSION_EDIT may toggle checkboxes (not only ASSIGN/REMOVE).
 * On the add form, USER_ROLE_PERMISSION_ADD may toggle checkboxes.
 */
export function hasCrudPermission(permissions, action) {
  if (!action) return false;
  if (!Array.isArray(permissions) || permissions.length === 0) return false;

  const actionTokens = String(action)
    .split("||")
    .map((token) => String(token).trim().toUpperCase())
    .filter(Boolean);

  const normalizedPermissions = permissions.map((permission) =>
    String(permission).trim().toUpperCase()
  );

  return actionTokens.some((token) => normalizedPermissions.includes(token));
}

/** USER_ROLE_PERMISSION_ALL is list-only — granular actions must not inherit ALL via fallback. */
function skipsAllFallbackForToken(token) {
  const upper = String(token).trim().toUpperCase();
  return (
    upper.startsWith("USER_ROLE_PERMISSION_") &&
    upper !== "USER_ROLE_PERMISSION" &&
    upper !== "USER_ROLE_PERMISSION_ALL"
  );
}

/**
 * For list delete/edit: match exact DELETE/EDIT permission, or module *_ALL
 * (e.g. DEPARTMENT_ALL grants DEPARTMENT_DELETE on the list).
 * USER_ROLE_PERMISSION_ADD/EDIT/DELETE/ASSIGN/REMOVE do not fall back to USER_ROLE_PERMISSION_ALL.
 */
export function hasCrudOrAllPermission(permissions, action) {
  if (hasCrudPermission(permissions, action)) {
    return true;
  }
  if (!action || !Array.isArray(permissions) || permissions.length === 0) {
    return false;
  }

  const normalizedPermissions = permissions.map((permission) =>
    String(permission).trim().toUpperCase()
  );

  return String(action)
    .split("||")
    .map((token) => String(token).trim().toUpperCase())
    .filter(Boolean)
    .some((token) => {
      if (skipsAllFallbackForToken(token)) {
        return false;
      }

      const moduleAll = token.replace(/_(ADD|EDIT|DELETE)$/, "_ALL");
      if (moduleAll !== token && normalizedPermissions.includes(moduleAll)) {
        return true;
      }

      if (token.startsWith("DELETE_")) {
        const moduleAllFromPrefix = `${token.slice("DELETE_".length)}_ALL`;
        return normalizedPermissions.includes(moduleAllFromPrefix);
      }

      if (token.startsWith("ADD_")) {
        const moduleAllFromPrefix = `${token.slice("ADD_".length)}_ALL`;
        return normalizedPermissions.includes(moduleAllFromPrefix);
      }

      if (token.startsWith("EDIT_")) {
        const moduleAllFromPrefix = `${token.slice("EDIT_".length)}_ALL`;
        return normalizedPermissions.includes(moduleAllFromPrefix);
      }

      return false;
    });
}

/** Route / view access: exact match or CRUD-style ALL fallback. */
export function canAccessRoute(permissions, action) {
  if (!action) return false;
  return (
    hasPermissionAction(permissions, action) ||
    hasCrudOrAllPermission(permissions, action)
  );
}

/** View (eye) icon on list tables. */
export function hasViewPermission(permissions, viewAction) {
  if (!viewAction) return false;
  return (
    hasPermissionAction(permissions, viewAction) ||
    hasCrudOrAllPermission(permissions, viewAction)
  );
}

/** True when an action token is a delete permission (not edit). */
export function isDeletePermissionToken(action) {
  const upper = String(action).trim().toUpperCase();
  return upper.includes("DELETE") && !upper.includes("EDIT");
}

/**
 * edit_delete_action arrays must be [edit, delete] but some lists were reversed.
 * Auto-correct when index 0 looks like DELETE and index 1 looks like EDIT.
 */
export function resolveEditDeleteActionTokens(editDeleteAction) {
  if (!Array.isArray(editDeleteAction) || editDeleteAction.length === 0) {
    return { editAction: null, deleteAction: null };
  }

  if (editDeleteAction.length === 1) {
    const only = editDeleteAction[0];
    if (isDeletePermissionToken(only)) {
      return { editAction: null, deleteAction: only };
    }
    return { editAction: only, deleteAction: null };
  }

  const first = editDeleteAction[0];
  const second = editDeleteAction[1];
  const firstIsDelete = isDeletePermissionToken(first);
  const secondIsDelete = isDeletePermissionToken(second);

  if (firstIsDelete && !secondIsDelete) {
    return { editAction: second, deleteAction: first };
  }

  return { editAction: first, deleteAction: second };
}

export function getOrganizationUserId(userData) {
  if (!userData) return null;

  const activeOrgId = Number(
    userData?.organization?.organization_id ??
      userData?.user_active_organization?.organization_id ??
      userData?.user_active_organization?.user_current_active_organization_id
  );

  if (Number.isFinite(activeOrgId) && activeOrgId > 0) {
    const matched = (userData?.metadata || []).find(
      (item) => Number(item?.organization_id) === activeOrgId
    );
    if (matched?.organization_user_id) {
      return matched.organization_user_id;
    }
  }

  return (
    userData?.organizationuser?.organization_user_id ??
    userData?.metadata?.[0]?.organization_user_id ??
    null
  );
}

export function canViewOrganizationPage(permissions) {
  return hasPermissionAction(permissions, "ORGANIZATION_ALL");
}

export function canSwitchOrganization(permissions) {
  return hasPermissionAction(permissions, "Switch_Organization");
}

/** Sidebar + route access to Organizations list. */
export function canAccessOrganizationList(permissions) {
  return (
    canViewOrganizationPage(permissions) || canSwitchOrganization(permissions)
  );
}

/** Organizations the user may access for the current app (from login metadata). */
export function getAllowedOrganizations(user) {
  const fromStore = user?.organizations;
  const fromLogin = user?.organization;
  let allOrganizations = [];
  if (Array.isArray(fromStore) && fromStore.length > 0) {
    allOrganizations = fromStore;
  } else if (Array.isArray(fromLogin) && fromLogin.length > 0) {
    allOrganizations = fromLogin;
  } else if (fromLogin && typeof fromLogin === "object") {
    allOrganizations = [fromLogin];
  }

  const allowedOrgIds = new Set(
    (user?.metadata || [])
      .map((item) => Number(item?.organization_id))
      .filter(Boolean)
  );

  if (!allowedOrgIds.size) {
    return allOrganizations;
  }

  return allOrganizations.filter((org) =>
    allowedOrgIds.has(Number(org?.organization_id))
  );
}

export function getAllowedOrganizationIds(user) {
  return new Set(
    getAllowedOrganizations(user).map((org) => Number(org.organization_id))
  );
}
